import {
  createRouter,
  createWebHistory,
  NavigationGuard,
  NavigationGuardReturn,
  RouteRecordRaw,
  RouteLocationNormalizedGeneric
} from 'vue-router'

import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth'
import { validateHierarchy } from './hierarchy'
import store from '@/vuex/index.js'
import { can } from '@/composables/useCan'

import RootPage from '@/pages/Home/RootPage.vue'
import AuthOverview from '@/pages/Auth/Overview.vue'
import SignInView from '@/pages/Auth/SignInView.vue'
import SignUpView from '@/pages/Auth/SignUpView.vue'
import Logout from '@/pages/Logout/index.vue'

import Invite from '@/pages/Invite/index.vue'

import Profile from '@/pages/User/index.vue'
import Dashboard from '@/pages/Dashboard/index.vue'

import FeatureLayout from '@/pages/Feature/layout.vue'
import FeatureIndex from '@/pages/Feature/index.vue'
import FeatureEdit from '@/pages/Feature/Edit/index.vue'

import OrgLayout from '@/pages/Organization/layout.vue'
import OrgMembers from '@/pages/Organization/Members/index.vue'
import OrgRoles from '@/pages/Organization/Roles/index.vue'
import OrgRoleEdit from '@/pages/Organization/Roles/Edit.vue'
import OrgSettings from '@/pages/Organization/Settings/index.vue'
import OrgApiKeys from '@/pages/Organization/ApiKeys/index.vue'
import OrgBilling from '@/pages/Organization/Billing/index.vue'
import ProjectLayout from '@/pages/Project/layout.vue'
import ProjectSettingsLayout from '@/pages/Project/Settings/layout.vue'
import ProjectHome from '@/pages/Project/index.vue'
import ProjectMonitoring from '@/pages/Project/Monitoring/index.vue'
import ProjectAlerts from '@/pages/Project/Alerts/index.vue'
import ProjectImagery from '@/pages/Project/Imagery/index.vue'
import ProjectChat from '@/pages/Project/Chat/index.vue'
import ProjectChatSettings from '@/pages/Project/Settings/Chat.vue'
import ProjectAgentsSettings from '@/pages/Project/Settings/Agents.vue'
import ProjectMonitoringSettings from '@/pages/Project/MonitoringSettings/index.vue'
import ProjectTemplates from '@/pages/Project/Templates/index.vue'
import ProjectSkills from '@/pages/Project/Skills/index.vue'
import CreateTemplatePage from '@/pages/Project/Templates/CreateTemplatePage.vue'
import ProjectMembers from '@/pages/Project/Members/index.vue'
import ProjectEdit from '@/pages/Project/Edit/index.vue'
import ProjectCreate from '@/pages/Project/Create/index.vue'

import LayersLayout from '@/pages/Layers/layout.vue'
import EmptyLayout from '@/pages/Empty/layout.vue'

import Admin from '@/pages/Admin/index.vue'
import AdminUsers from '@/pages/Admin/Users/index.vue'
import AdminOrganizations from '@/pages/Admin/Organizations/index.vue'
import AdminTemplates from '@/pages/Admin/Templates/index.vue'
import AdminWorkflows from '@/pages/Admin/Workflows/index.vue'
import AdminSkills from '@/pages/Admin/Skills/index.vue'
import AdminAgents from '@/pages/Admin/Agents/index.vue'
import AdminPayments from '@/pages/Admin/Payments/index.vue'
import AdminPlanet from '@/pages/Admin/Planet/index.vue'
import AdminEmails from '@/pages/Admin/Emails/index.vue'
import AdminPrompts from '@/pages/Admin/Prompts/index.vue'

import FileViewer from '@/pages/FileViewer/index.vue'

import LayerCreate from '@/pages/Layers/Create/index.vue'
import LayerDetail from '@/pages/Layers/Detail/index.vue'
import MapMembers from '@/pages/Maps/Members/index.vue'
import MapCreate from '@/pages/Maps/Create/index.vue'

import ReportDetail from '@/pages/Report/index.vue'
import NoService from '@/pages/Error/NoService.vue'

import { features } from '@/config/features'

// Wait for Firebase to restore auth state (resolves once on first call, instant after)
let authReady: Promise<void> | null = null
function waitForAuth(): Promise<void> {
  if (!authReady) {
    authReady = new Promise(resolve => {
      const unsubscribe = onAuthStateChanged(getAuth(), () => {
        unsubscribe()
        resolve()
      })
    })
  }
  return authReady
}

// Wait for the Vuex member to be loaded after handleAuthentication completes.
// Firebase auth resolves before the /member API call finishes, so requireAdmin
// must wait for isLoggedIn (set only after setMember) or confirm there's no user.
// Resolves on: success (isLoggedIn true), Firebase sign-out (user null), or 10s timeout.
function waitForMember(): Promise<void> {
  return new Promise(resolve => {
    if (store.getters.getIsLoggedIn || !getAuth().currentUser) {
      resolve()
      return
    }
    let storeUnsubscribe: (() => void) | undefined
    let authUnsubscribe: (() => void) | undefined
    let timeout: ReturnType<typeof setTimeout> | undefined
    function cleanup() {
      storeUnsubscribe?.()
      authUnsubscribe?.()
      clearTimeout(timeout)
    }
    storeUnsubscribe = store.watch(
      (_state: any, getters: any) => getters.getIsLoggedIn,
      (isLoggedIn: boolean) => {
        if (isLoggedIn) { cleanup(); resolve() }
      }
    )
    authUnsubscribe = onAuthStateChanged(getAuth(), (user) => {
      if (!user) { cleanup(); resolve() }
    })
    timeout = setTimeout(() => { cleanup(); resolve() }, 10_000)
  })
}

type RouteGuard = (
  to: RouteLocationNormalizedGeneric,
  from: RouteLocationNormalizedGeneric,
  ...args: any[]
) => NavigationGuardReturn | Promise<NavigationGuardReturn>

const nextPreserveQueryParams: RouteGuard = (
  to: RouteLocationNormalizedGeneric,
  from: RouteLocationNormalizedGeneric,
) => {
  // Exclude 'redirect' from preserved params to prevent stacking
  const { redirect, ...fromQuery } = from.query
  const isRedirectionNeeded = Object.keys(fromQuery).length > 0 && JSON.stringify(to.query) !== JSON.stringify(fromQuery)
  if (isRedirectionNeeded) {
    return { path: to.path, query: fromQuery }
  }
}

const requireAuth: RouteGuard = async (
  to: RouteLocationNormalizedGeneric,
  from: RouteLocationNormalizedGeneric,
) => {
  await waitForAuth()
  const auth = getAuth()
  const user = auth.currentUser

  if (!user) {
    return {
      path: '/signin',
      query: { redirect: to.fullPath }
    }
  } else {
    return nextPreserveQueryParams(to, from)
  }
}

const requireAdmin: RouteGuard = async (
  to: RouteLocationNormalizedGeneric,
  from: RouteLocationNormalizedGeneric,
) => {
  await waitForAuth()
  const auth = getAuth()
  if (!auth.currentUser) return { path: '/signin', query: { redirect: to.fullPath } }
  await waitForMember()
  const member = store.getters['user/getMember']
  if (!member?.is_super_user) return { path: '/' }
  return nextPreserveQueryParams(to, from)
}

const logout: NavigationGuard = async () => {
  const auth = getAuth()
  await signOut(auth)
}

const requireRolesAccess: RouteGuard = async (
  to: RouteLocationNormalizedGeneric,
) => {
  if (!can('org_role', 'read', { orgId: to.params.org_id as string })) {
    return { name: 'OrgMembers', params: { org_id: to.params.org_id } }
  }
}

const requireBillingEnabled: RouteGuard = async (
  to: RouteLocationNormalizedGeneric,
  from: RouteLocationNormalizedGeneric,
) => {
  if (!features.stripe.showBillingPage) {
    return { name: 'OrgMembers', params: to.params }
  } else {
    return requireAuth(to, from)
  }
}

const rts: RouteRecordRaw[] = [
  {
    path: '/auth',
    redirect: '/signin',
  },
  {
    path: '/auth/overview',
    redirect: '/account/select',
  },
  {
    path: '/sign-in',
    redirect: '/signin',
  },
  {
    path: '/signin',
    name: 'SignIn',
    component: SignInView,
    meta: {
      layout: 'fullscreen',
      showBackgroundUI: false,
      title: 'Sign In',
      isAuthPage: true,
    }
  },
  {
    path: '/account/select',
    name: 'AuthOverview',
    component: AuthOverview,
    beforeEnter: requireAuth,
    meta: {
      title: 'Select Organization',
      layout: 'fullscreen',
      showBackgroundUI: false
    }
  },
  {
    path: '/sign-up',
    redirect: '/signup',
  },
  {
    path: '/signup',
    name: 'SignUp',
    component: SignUpView,
    meta: {
      layout: 'fullscreen',
      showBackgroundUI: false,
      title: 'Sign Up',
      isAuthPage: true,
    }
  },
  {
    path: '/invite/:inviteCode',
    name: 'Invite',
    component: Invite,
    meta: {
      title: 'Invite',
      layout: 'fullscreen',
      showBackgroundUI: false
    },
  },
  {
    path: '/map-invite/:inviteCode',
    name: 'MapInvite',
    component: Invite,
    meta: {
      title: 'Map Invite',
      layout: 'fullscreen',
      showBackgroundUI: false
    },
  },
  {
    path: '/user',
    name: 'User',
    component: Profile,
    beforeEnter: requireAuth,
    meta: {
      title: 'Profile Settings',
      pageSize: 'low'
    },
  },
  {
    path: '/',
    name: 'Dashboard',
    component: RootPage,
    meta: {
      title: 'Dashboard',
      layout: 'fullscreen',
      showBackgroundUI: false,
    }
  },
  {
    path: '/organizations/:org_id/projects/create',
    name: 'CreateProject',
    component: ProjectCreate,
    beforeEnter: requireAuth,
    meta: {
      title: 'Create New Project',
      pageSize: 'low'
    }
  },
  {
    path: '/organizations/:org_id/projects/:project_id/features/:featureCode',
    name: 'Feature',
    component: FeatureLayout,
    redirect: {name: 'FeatureIndex'},
    beforeEnter: requireAuth,
    children: [
      {
        path: '',
        name: 'FeatureIndex',
        component: FeatureIndex,
        meta: {
          title: 'Feature',
          pageSize: 'low'
        }
      },
      {
        path: 'edit',
        name: 'FeatureEdit',
        component: FeatureEdit,
        meta: {
          title: 'Edit Feature',
          pageSize: 'low'
        }
      }
    ]
  },
  {
    path: '/organizations/:org_id/file-viewer',
    name: 'FileViewer',
    component: FileViewer,
    beforeEnter: requireAuth,
    meta: {
      title: 'Import Data',
      layout: 'float-big',
      pageSize: 'medium'
    }
  },
  {
    path: '/organizations/:org_id',
    name: 'Organization',
    component: OrgLayout,
    redirect: {name: 'OrgProjects'},
    beforeEnter: requireAuth,
    meta: {
      layout: 'float-big',
      pageSize: 'medium',
      orgSettings: true
    },
    children: [
      {
        path: 'members',
        name: 'OrgMembers',
        component: OrgMembers,
        meta: {
          title: 'Settings',
          layout: 'float-big',
          pageSize: 'medium'
        }
      },
      {
        path: 'roles',
        name: 'OrgRoles',
        component: OrgRoles,
        beforeEnter: requireRolesAccess,
        meta: {
          title: 'Settings',
          layout: 'float-big',
          pageSize: 'medium'
        }
      },
      {
        path: 'roles/:role_id',
        name: 'OrgRoleEdit',
        component: OrgRoleEdit,
        beforeEnter: requireRolesAccess,
        meta: {
          title: 'Settings',
          layout: 'float-big',
          pageSize: 'medium',
          navTab: 'OrgRoles'
        }
      },
      {
        path: 'settings',
        name: 'OrgSettings',
        component: OrgSettings,
        meta: {
          title: 'Settings',
          layout: 'float-big',
          pageSize: 'medium'
        }
      },
      {
        path: 'api-keys',
        name: 'OrgApiKeys',
        component: OrgApiKeys,
        meta: {
          title: 'Settings',
          layout: 'float-big',
          pageSize: 'medium'
        }
      },
      {
        path: 'billing',
        name: 'OrgBilling',
        component: OrgBilling,
        beforeEnter: requireBillingEnabled,
        meta: {
          title: 'Settings',
          layout: 'float-big',
          pageSize: 'medium'
        }
      },
    ]
  },
  {
    path: '/organizations/:org_id/projects',
    name: 'OrgProjects',
    component: EmptyLayout,
    beforeEnter: requireAuth,
    meta: {
      title: 'Projects',
    }
  },
  {
    path: '/organizations/:org_id/projects/places/:place+',
    name: 'OrgProjectPlaces',
    component: EmptyLayout,
    beforeEnter: requireAuth,
    meta: {
      title: 'Projects',
    }
  },
  {
    path: '/organizations/:org_id/maps',
    name: 'OrgLayers',
    component: EmptyLayout,
    beforeEnter: requireAuth,
    meta: {
      title: 'Layers',
    }
  },
  {
    path: '/organizations/:org_id/chats/:conversation_uuid?',
    name: 'OrgChats',
    component: EmptyLayout,
    beforeEnter: requireAuth,
    meta: {
      title: 'Chats',
    }
  },
  {
    path: '/organizations/:org_id/reports',
    name: 'OrgReportsPage',
    component: EmptyLayout,
    beforeEnter: requireAuth,
    meta: {
      title: 'Reports',
    }
  },
  {
    path: '/organizations/:org_id/payments',
    name: 'OrgPayments',
    component: EmptyLayout,
    beforeEnter: requireAuth,
    meta: {
      title: 'Payments',
    }
  },
  {
    path: '/organizations/:org_id/kyc',
    name: 'OrgKyc',
    component: EmptyLayout,
    beforeEnter: requireAuth,
    meta: {
      title: 'Identity Verification',
    }
  },
  {
    path: '/organizations/:org_id/reports/:report_uuid',
    name: 'ReportDetail',
    component: ReportDetail,
    beforeEnter: requireAuth,
    meta: {
      title: 'Report',
      layout: 'float-big',
      pageSize: 'medium'
    }
  },
  {
    path: '/organizations/:org_id/layers/create',
    name: 'LayerCreate',
    component: LayerCreate,
    beforeEnter: requireAuth,
    meta: {
      title: 'New Layer',
      pageSize: 'low'
    }
  },
  {
    path: '/organizations/:org_id/layers/:layer_id',
    name: 'LayerDetail',
    component: LayerDetail,
    beforeEnter: requireAuth,
    meta: {
      title: 'Layer Detail',
      pageSize: 'low'
    }
  },
  {
    path: '/organizations/:org_id/maps/create',
    name: 'MapCreate',
    component: MapCreate,
    beforeEnter: requireAuth,
    meta: {
      title: 'New Map',
      pageSize: 'low'
    }
  },
  {
    path: '/organizations/:org_id/maps/:map_id',
    name: 'MapEdit',
    component: MapCreate,
    beforeEnter: requireAuth,
    meta: {
      title: 'Settings',
      pageSize: 'low'
    }
  },
  {
    path: '/organizations/:org_id/maps/:map_id/members',
    name: 'MapMembers',
    component: MapMembers,
    beforeEnter: requireAuth,
    meta: {
      title: 'Map Members',
      layout: 'float-big',
      pageSize: 'medium'
    }
  },
  {
    path: '/organizations/:org_id/projects/:project_id',
    name: 'Project',
    component: ProjectLayout,
    redirect: {name: 'ProjectHome'},
    beforeEnter: requireAuth,
    children: [
      {
        path: '',
        name: 'ProjectHome',
        component: ProjectHome,
        meta: {
          title: 'Data',
          pageSize: 'low'
        }
      },
      {
        path: 'monitoring',
        name: 'ProjectMonitoring',
        component: ProjectMonitoring,
        meta: {
          title: 'Monitoring',
          pageSize: 'low',
          areaSelection: true
        }
      },
      {
        path: 'alerts',
        name: 'ProjectAlerts',
        component: ProjectAlerts,
        meta: {
          title: 'Alerts',
          pageSize: 'low',
          areaSelection: true
        }
      },
      {
        path: 'imagery',
        name: 'ProjectImagery',
        component: ProjectImagery,
        meta: {
          title: 'Imagery',
          pageSize: 'low',
          areaSelection: true
        }
      },
      {
        path: 'chat',
        name: 'ProjectChat',
        component: ProjectChat,
        meta: {
          title: 'Chat',
          pageSize: 'low'
        }
      },
      {
        path: 'create',
        name: 'ProjectCreate',
        component: ProjectCreate,
        meta: {
          title: 'New Project',
          pageSize: 'low'
        }
      },
      {
        path: 'settings',
        name: 'ProjectSettings',
        component: ProjectSettingsLayout,
        redirect: {name: 'ProjectEdit'},
        children: [
          {
            path: 'edit',
            name: 'ProjectEdit',
            component: ProjectEdit,
            meta: {
              layout: 'float-big',
              title: 'Settings',
              pageSize: 'medium'
            }
          },
          {
            path: 'members',
            name: 'ProjectMembers',
            component: ProjectMembers,
            meta: {
              layout: 'float-big',
              title: 'Settings',
              pageSize: 'medium'
            }
          },
          {
            path: 'roles',
            name: 'ProjectRoles',
            redirect: to => ({ name: 'OrgRoles', params: { org_id: to.params.org_id }, query: { scope: 'project' } }),
          },
          {
            path: 'roles/:role_id',
            name: 'ProjectRoleEdit',
            redirect: to => ({ name: 'OrgRoleEdit', params: { org_id: to.params.org_id, role_id: to.params.role_id } }),
          },
          {
            path: 'templates',
            name: 'ProjectTemplates',
            component: ProjectTemplates,
            meta: {
              layout: 'float-big',
              title: 'Settings',
              pageSize: 'medium'
            }
          },
          {
            path: 'monitoring',
            name: 'ProjectMonitoringSettings',
            component: ProjectMonitoringSettings,
            meta: {
              layout: 'float-big',
              title: 'Settings',
              pageSize: 'medium'
            }
          },
          {
            path: 'skills',
            name: 'ProjectSkills',
            component: ProjectSkills,
            meta: {
              layout: 'float-big',
              title: 'Settings',
              pageSize: 'medium'
            }
          },
          {
            path: 'chat',
            name: 'ProjectChatSettings',
            component: ProjectChatSettings,
            meta: {
              layout: 'float-big',
              title: 'Settings',
              pageSize: 'medium'
            }
          },
          {
            path: 'agents',
            name: 'ProjectAgentsSettings',
            component: ProjectAgentsSettings,
            meta: {
              layout: 'float-big',
              title: 'Settings',
              pageSize: 'medium'
            }
          }
        ]
      },
      // Pre-restructure URLs (bookmarks / external links) moved under /settings/*
      { path: 'edit', redirect: { name: 'ProjectEdit' } },
      { path: 'members', redirect: { name: 'ProjectMembers' } },
      { path: 'templates', redirect: { name: 'ProjectTemplates' } },
      { path: 'skills', redirect: { name: 'ProjectSkills' } },
      {
        path: 'templates/create',
        name: 'CreateTemplate',
        component: CreateTemplatePage,
        meta: {
          layout: 'float-big',
          title: 'Create Template',
          pageSize: 'medium'
        }
      },
      {
        path: 'templates/:template_id/edit',
        name: 'EditTemplate',
        component: CreateTemplatePage,
        meta: {
          layout: 'float-big',
          title: 'Edit Template',
          pageSize: 'medium'
        }
      }
    ]
  },
  {
    // Top-level (NOT a Project child) so ProjectLayout's header chrome doesn't appear.
    path: '/organizations/:org_id/projects/:project_id/import',
    name: 'ProjectImport',
    component: FileViewer,
    beforeEnter: requireAuth,
    meta: {
      title: 'Import Data',
      layout: 'float-big',
      pageSize: 'medium'
    }
  },
  {
    path: '/admin/',
    name: 'Admin',
    component: Admin,
    beforeEnter: requireAdmin,
    meta: {
      title: 'Admin Tools',
      layout: 'float-big',
      pageSize: 'medium'
    }
  },
  {
    path: '/admin/users',
    name: 'AdminUsers',
    component: AdminUsers,
    beforeEnter: requireAdmin,
    meta: {
      title: 'Admin - Members',
      layout: 'full',
      pageSize: 'off'
    }
  },
  {
    path: '/admin/organizations',
    name: 'AdminOrganizations',
    component: AdminOrganizations,
    beforeEnter: requireAdmin,
    meta: {
      title: 'Admin - Organizations',
      layout: 'full',
      pageSize: 'off'
    }
  },
  {
    path: '/admin/templates',
    name: 'AdminTemplates',
    component: AdminTemplates,
    beforeEnter: requireAdmin,
    meta: {
      title: 'Admin - Templates',
      layout: 'full',
      pageSize: 'off'
    }
  },
  {
    path: '/admin/templates/create',
    name: 'AdminCreateTemplate',
    component: CreateTemplatePage,
    beforeEnter: requireAdmin,
    meta: {
      title: 'Admin - Create Template',
      layout: 'full',
      pageSize: 'off'
    }
  },
  {
    path: '/admin/templates/:template_id/edit',
    name: 'AdminEditTemplate',
    component: CreateTemplatePage,
    beforeEnter: requireAdmin,
    meta: {
      title: 'Admin - Edit Template',
      layout: 'full',
      pageSize: 'off'
    }
  },
  {
    path: '/admin/workflows',
    name: 'AdminWorkflows',
    component: AdminWorkflows,
    beforeEnter: requireAdmin,
    meta: {
      title: 'Admin - Workflows',
      layout: 'full',
      pageSize: 'off'
    }
  },
  {
    path: '/admin/skills',
    name: 'AdminSkills',
    component: AdminSkills,
    beforeEnter: requireAdmin,
    meta: {
      title: 'Admin - Skills',
      layout: 'full',
      pageSize: 'off'
    }
  },
  {
    path: '/admin/agents',
    name: 'AdminAgents',
    component: AdminAgents,
    beforeEnter: requireAdmin,
    meta: {
      title: 'Admin - Agents',
      layout: 'full',
      pageSize: 'off'
    }
  },
  {
    path: '/admin/prompts',
    name: 'AdminPrompts',
    component: AdminPrompts,
    beforeEnter: requireAdmin,
    meta: {
      title: 'Admin - Prompts',
      layout: 'full',
      pageSize: 'off'
    }
  },
  {
    path: '/admin/payments',
    name: 'AdminPayments',
    component: AdminPayments,
    beforeEnter: requireAdmin,
    meta: {
      title: 'Admin - Payments',
      layout: 'fullscreen'
    }
  },
  {
    path: '/admin/bridge',
    redirect: '/admin/payments'
  },
  {
    path: '/admin/planet',
    name: 'AdminPlanet',
    component: AdminPlanet,
    beforeEnter: requireAdmin,
    meta: {
      title: 'Admin - Planet Products',
      layout: 'full',
      pageSize: 'medium'
    }
  },
  {
    path: '/admin/emails',
    name: 'AdminEmails',
    component: AdminEmails,
    beforeEnter: requireAdmin,
    meta: {
      title: 'Admin - Email Previews',
      layout: 'fullscreen'
    }
  },
  {
    path: '/payments/me',
    redirect: '/admin/payments'
  },
  {
    path: '/logout',
    name: 'Logout',
    component: Logout,
    meta: {
      title: 'Logout'
    }
  },
  {
    path: '/no-service',
    name: 'NoService',
    component: NoService,
    meta: {
      title: 'Connection Error',
      layout: 'modal',
      showBackgroundUI: false
    }
  },
  {
    path: '/:catchAll(.*)',
    redirect: '/',
    beforeEnter: requireAuth
  }
]

const baseUrl = window.location.pathname + window.location.search
let history = null
if (window.location.search.includes('oobCode')) {
  history = createWebHistory(baseUrl)
} else {
  history = createWebHistory()
}

export const router = createRouter({
  history: history,
  routes: rts
})

export const ensureOrgAccess = async (to: RouteLocationNormalizedGeneric) => {
  if (!to.params.org_id) return
  try {
    await store.dispatch('access/ensure', to.params.org_id)
  } catch {
    // Checks deny until a later navigation or refresh loads the view.
  }
}

router.beforeEach(ensureOrgAccess)

if (import.meta.env.DEV) {
  validateHierarchy(router)
}

export default router
